// src/pages/Leads.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLeads, deleteLead, uploadLeads } from '../services/leadsService';
import { getCustomFields } from '../services/settingsService';
import './Leads.css';

const Leads = () => {
    const [leads, setLeads] = useState([]);
    const [filteredLeads, setFilteredLeads] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);

    // For CSV upload modal state.
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);

    // Campaign ID input state (required for CSV upload).
    const [campaignId, setCampaignId] = useState('');

    // State for custom fields available and selected to display as extra columns.
    const [availableCustomFields, setAvailableCustomFields] = useState([]);
    const [selectedCustomFields, setSelectedCustomFields] = useState([]);
    const [isAddColumnsModalOpen, setIsAddColumnsModalOpen] = useState(false);

    const navigate = useNavigate();

    // Fetch all leads from the backend
    const fetchLeads = async () => {
        setLoading(true);
        try {
            const response = await getLeads();
            setLeads(response.data.leads);
            setFilteredLeads(response.data.leads);
        } catch (error) {
            console.error('Error fetching leads:', error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch available custom fields (from Settings)
    const fetchCustomFields = async () => {
        try {
            const response = await getCustomFields();
            setAvailableCustomFields(response.data.customFields || []);
        } catch (error) {
            console.error('Error fetching custom fields:', error);
        }
    };

    useEffect(() => {
        fetchLeads();
        fetchCustomFields();
    }, []);

    // Handle search input to filter leads by name or phone.
    const handleSearch = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        if (term.trim() === '') {
            setFilteredLeads(leads);
        } else {
            const lowerTerm = term.toLowerCase();
            const filtered = leads.filter(
                (lead) =>
                    (lead.firstName && lead.firstName.toLowerCase().includes(lowerTerm)) ||
                    (lead.lastName && lead.lastName.toLowerCase().includes(lowerTerm)) ||
                    (lead.phone && lead.phone.toString().includes(lowerTerm))
            );
            setFilteredLeads(filtered);
        }
    };

    // Delete a lead by its ID.
    const handleDeleteLead = async (leadId) => {
        if (window.confirm('Are you sure you want to delete this lead?')) {
            try {
                await deleteLead(leadId);
                fetchLeads();
            } catch (error) {
                console.error('Error deleting lead:', error);
            }
        }
    };

    // Handlers for the CSV upload modal.
    const openUploadModal = () => {
        setUploadFile(null);
        setIsUploadModalOpen(true);
    };

    const closeUploadModal = () => {
        setIsUploadModalOpen(false);
    };

    const handleFileChange = (e) => {
        setUploadFile(e.target.files[0]);
    };

    const handleUploadLeads = async () => {
        if (!uploadFile) {
            alert('Please select a CSV file to upload.');
            return;
        }
        if (!campaignId.trim()) {
            alert('Please enter a Campaign ID.');
            return;
        }
        const formData = new FormData();
        formData.append('file', uploadFile);
        formData.append('campaignId', campaignId);
        try {
            await uploadLeads(formData);
            alert('Leads uploaded successfully.');
            closeUploadModal();
            fetchLeads();
        } catch (error) {
            console.error('Error uploading leads:', error);
            alert('Error uploading leads.');
        }
    };

    // Handlers for the "Add Columns" modal (to choose custom field columns)
    const openAddColumnsModal = () => {
        setIsAddColumnsModalOpen(true);
    };

    const closeAddColumnsModal = () => {
        setIsAddColumnsModalOpen(false);
    };

    const handleCustomFieldCheckboxChange = (fieldId) => {
        if (selectedCustomFields.includes(fieldId)) {
            setSelectedCustomFields(selectedCustomFields.filter(id => id !== fieldId));
        } else {
            setSelectedCustomFields([...selectedCustomFields, fieldId]);
        }
    };

    // Utility: get the custom field object by its ID.
    const getCustomFieldById = (id) => {
        return availableCustomFields.find(field => field.id === id);
    };

    return (
        <div style={{ padding: '20px' }}>
            <h2>All Leads</h2>

            {/* Campaign ID Input */}
            <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
                <label style={{ marginRight: '10px', fontWeight: '700' }}>Campaign ID:</label>
                <input
                    type="text"
                    value={campaignId}
                    onChange={(e) => setCampaignId(e.target.value)}
                    placeholder="Enter Campaign ID"
                    style={{
                        padding: '0.5rem',
                        width: '200px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                    }}
                />
            </div>

            {/* Search and Buttons */}
            <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center' }}>
                <input
                    type="text"
                    placeholder="Search leads by name or phone"
                    value={searchTerm}
                    onChange={handleSearch}
                    style={{
                        padding: '0.5rem',
                        width: '100%',
                        maxWidth: '400px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                    }}
                />
                <button className="create-campaign-btn" onClick={openUploadModal} style={{ marginLeft: '10px' }}>
                    Upload Leads
                </button>
                <button className="create-campaign-btn" onClick={openAddColumnsModal} style={{ marginLeft: '10px' }}>
                    Add Columns
                </button>
            </div>

            {/* Leads Table */}
            {loading ? (
                <p>Loading leads...</p>
            ) : (
                <table className="campaign-table">
                    <thead>
                    <tr>
                        <th>ID</th>
                        <th>First Name</th>
                        <th>Last Name</th>
                        <th>Phone</th>
                        {selectedCustomFields.map(fieldId => {
                            const field = getCustomFieldById(fieldId);
                            return <th key={fieldId}>{field ? field.fieldName : 'Custom Field'}</th>;
                        })}
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filteredLeads.map((lead) => (
                        <tr key={lead.id}>
                            <td>{lead.id}</td>
                            <td>{lead.firstName}</td>
                            <td>{lead.lastName}</td>
                            <td>{lead.phone}</td>
                            {selectedCustomFields.map(fieldId => {
                                const field = getCustomFieldById(fieldId);
                                const key = field ? field.fieldName.toLowerCase().replace(/\s+/g, '_') : '';
                                // Try to get the value from the top-level, if not, check inside customFields.
                                const customValue =
                                    lead[key] || (lead.customFields && lead.customFields[key]) || '';
                                return <td key={fieldId}>{customValue}</td>;
                            })}
                            <td>
                                <button
                                    className="edit-btn"
                                    onClick={() => navigate(`/leads/${lead.id}`)}
                                    style={{ marginRight: '5px' }}
                                >
                                    View
                                </button>
                                <button className="edit-btn" onClick={() => handleDeleteLead(lead.id)}>
                                    Delete
                                </button>
                            </td>
                        </tr>
                    ))}
                    {filteredLeads.length === 0 && (
                        <tr>
                            <td colSpan={5 + selectedCustomFields.length} style={{ textAlign: 'center' }}>
                                No leads found.
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            )}

            {/* Modal for Uploading Leads */}
            {isUploadModalOpen && (
                <div className="modal">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>Upload Leads</h3>
                        </div>
                        <div className="modal-body">
                            <p>
                                Select a CSV file with columns: <strong>firstName, lastName, phoneNumber</strong>
                            </p>
                            <input type="file" accept=".csv" onChange={handleFileChange} />
                        </div>
                        <div className="modal-footer">
                            <button onClick={closeUploadModal}>Cancel</button>
                            <button onClick={handleUploadLeads}>Upload Leads</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal for Adding Custom Columns */}
            {isAddColumnsModalOpen && (
                <div className="modal">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>Add Custom Columns</h3>
                        </div>
                        <div className="modal-body">
                            <p>Select custom fields to add as columns:</p>
                            {availableCustomFields.length === 0 ? (
                                <p>No custom fields available. Create some in Settings.</p>
                            ) : (
                                <ul style={{ listStyle: 'none', padding: 0 }}>
                                    {availableCustomFields.map(field => (
                                        <li key={field.id}>
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedCustomFields.includes(field.id)}
                                                    onChange={() => handleCustomFieldCheckboxChange(field.id)}
                                                />
                                                {field.fieldName} ({field.fieldType})
                                            </label>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button onClick={closeAddColumnsModal}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Leads;
